import SimpleSchema from 'simpl-schema';

import store from '../../config/configureStore';
import { groupAOffenseCodes } from '../CategoryCodes/groupAOffenses';
import { groupBOffenses } from 'simpl-schema-validation/CategoryCodes/groupBOffenses';
import { biasMotivationMutuallyExclusiveCodes } from 'simpl-schema-validation/helpers/Codes/MutuallyExclusive';
import { getCategoryOfCode } from 'utils/functions';

export const offenseDetailsSchema = () => {
  SimpleSchema.setDefaultMessages({
    messages: {
      en: store?.store.getState().incident.ibrsValidationRules?.offense || {},
    },
  });

  return new SimpleSchema({
    incVictims: {
      type: Array,
      optional: true,
      custom() {
        const ibrsVersion = store.store.getState().user?.ibrsVersion;

        // NIBRS-2019.2-23 Victims
        if (groupAOffenseCodes.includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (!this.value.length) {
            return 'NIBRS-2019.2-23 Victims';
          }
        }

        // TEXAS-2023.1-06
        if (ibrsVersion === 'TX') {
          if (this.obj.offense.values.statuteDetails?.FBICode === '120') {
            if (!this.obj.incVictimTypes.includes('I')) {
              return 'TEXAS-2023.1-06';
            }
          }
        }
      },
    },
    'incVictims.$': {
      type: Number,
      optional: true,
      blackbox: true,
      custom() {},
    },

    incSubjects: {
      type: Array,
      optional: true,
      custom() {
        // NIBRS-2019.2-23: Report Entity Validation
        if (groupAOffenseCodes.includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (!this.value.length) {
            return 'NIBRS-2019.2-23 Subjects';
          }
        }

        // NIBRS-2019.2-162: Offender Sex
        if (this.obj.offense.values.statuteDetails?.FBICode === '11A') {
          const norm = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : null);
          const offenderPartiesSexes = new Set(
            this.obj.offenderParties.map((o) => norm(o.sex)).filter(Boolean)
          );
          const victimPartiesSexes = new Set(
            this.obj.victimParties.map((v) => norm(v.sex)).filter(Boolean)
          );

          // Check if “at least one offender must be a different sex than the victim(s)”
          const sexChecksOut =
            offenderPartiesSexes.size > 0 &&
            victimPartiesSexes.size > 0 &&
            [...offenderPartiesSexes].some((os) => [...victimPartiesSexes].some((vs) => os !== vs));

          if (!sexChecksOut) {
            return 'NIBRS-2019.2-162';
          }
        }
      },
    },
    'incSubjects.$': {
      type: Number,
      optional: true,
      blackbox: true,
      custom() {},
    },

    incArrestees: {
      type: Array,
      optional: true,
      custom() {
        // NIBRS-2019.2-194: NIBRS Group B Report segments
        // if (groupBOffenses.includes(this.obj.offense.values.statuteDetails?.FBICode)) {
        //   if (!this.value.length) {
        //     return 'NIBRS-2019.2-194';
        //   }
        // }
      },
    },
    'incArrestees.$': {
      type: Number,
      optional: true,
      blackbox: true,
      custom() {},
    },

    locationCategory: {
      type: String,
      label: 'NIBRS-2019.2-38: Location',
      optional: true,
      custom() {
        if (!this.value) {
          return 'NIBRS-2019.2-38';
        }
      },
    },

    structuresEntered: {
      type: SimpleSchema.Integer,
      optional: true,
      custom() {
        if (this.value && (this.value < 1 || this.value > 99)) return 'NIBRS-2019.2-41';

        //NIBRS-2019.2-40
        if (this.obj.offense.values.statuteDetails?.FBICode === '220') {
          const codeLocationCategories = store.store.getState().dictionary.codeLocationCategories;
          const locationCategory = getCategoryOfCode(
            codeLocationCategories,
            this.obj.offense.values.locationCategory
          );
          if (['14', '19'].includes(locationCategory)) {
            if (!this.value) {
              return 'NIBRS-2019.2-40';
            }
          }
        }
      },
    },

    biasMotivation: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-34: Offense Factor Bias Motivation
        if (groupAOffenseCodes.includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (!this.value || !JSON.parse(this.value)?.length) {
            if (this.obj.offense.values.statuteDetails?.FBICode !== '35A') {
              return 'NIBRS-2019.2-34';
            }
          }
        }

        // NIBRS-2019.2-209: Offense Factor Bias Motivation
        if (
          [...groupBOffenses, '0', '35A'].includes(this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (this.value && JSON.parse(this.value)?.length > 0) {
            return 'NIBRS-2019.2-209';
          }
        }

        if (!this.value) return;
        const parsedValues = JSON.parse(this.value);
        if (!parsedValues) return;
        const codeBiasMotivations = store.store.getState().dictionary.codeBiasMotivations;
        const biasMotivationCategories =
          parsedValues.map((value) => getCategoryOfCode(codeBiasMotivations, value)) || [];
        // NIBRS-2019.2-36: Offense Factor Bias Motivation
        let errorFlag = false;
        Object.keys(biasMotivationMutuallyExclusiveCodes).forEach((key) => {
          let count = 0;
          biasMotivationMutuallyExclusiveCodes[key].forEach((item) => {
            if (biasMotivationCategories.includes(item.category)) {
              count++;
            }
          });
          if (count > 1) {
            errorFlag = true;
          }
        });
        if (errorFlag) {
          return 'NIBRS-2019.2-36';
        }

        // NIBRS-2019.2-37: Offense Factor Bias Motivation
        if (this.obj.offense.values.statuteDetails?.FBICode === '09C') {
          if (biasMotivationCategories.includes('88')) {
            if (parsedValues.length > 1) {
              return 'NIBRS-2019.2-37';
            }
          } else {
            return 'NIBRS-2019.2-37';
          }
        }
      },
    },

    offenseAttempted: {
      type: Boolean,
      optional: true,
      custom() {
        // NIBRS-2019.2-30: Offense Attempted/Completed
        if (groupAOffenseCodes.includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (
            ['09A', '09B', '09C', '13A', '13B', '13B', '13C'].includes(
              this.obj.offense.values.statuteDetails?.FBICode
            )
          ) {
            if (this.value !== false) {
              return 'NIBRS-2019.2-30';
            }
          }
        }
      },
    },

    offenderSuspectedOfUsing: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-31: Offense Factor
        if (groupAOffenseCodes.includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (!this.value || !JSON.parse(this.value)?.length) {
            return 'NIBRS-2019.2-31';
          }
        }

        // NIBRS-2019.2-207: Offense Factor
        if ([...groupBOffenses, '0'].includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (this.value && JSON.parse(this.value)?.length) {
            return 'NIBRS-2019.2-207';
          }
        }

        if (!this.value) return;
        const parsedValues = JSON.parse(this.value);
        if (!parsedValues) return;

        const codeOffenseFactor = store.store.getState().dictionary.codeOffenseFactor;
        const suspectedOfUsingCategories =
          parsedValues.map((value) => getCategoryOfCode(codeOffenseFactor, value)) || [];

        // NIBRS-2019.2-33: Offense Factor
        if (suspectedOfUsingCategories.includes('n')) {
          if (parsedValues.length > 1) {
            return 'NIBRS-2019.2-33';
          }
        }
      },
    },

    forceCategory: {
      type: String,
      optional: true,
      custom() {
        const ibrsVersion = store.store.getState().user?.ibrsVersion;
        // NIBRS-2019.2-52: Weapon/Force Involved
        if (
          [
            '09A',
            '09B',
            '09C',
            '100',
            '11A',
            '11B',
            '11C',
            '11D',
            '120',
            '13A',
            '13B',
            '210',
            '520',
            '64A',
            '64B',
          ].includes(this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (!this.value || !JSON.parse(this.value)?.length) {
            return 'NIBRS-2019.2-52';
          }
        }

        if (!this.value) return;
        const parsedValues = JSON.parse(this.value);
        if (!parsedValues) return;

        const codeForceCategory = store.store.getState().dictionary.codeForceCategory;
        const forceCodeValues =
          parsedValues?.map((value) => getCategoryOfCode(codeForceCategory, value)) || [];

        // NIBRS-2019.2-51: Weapon/Force Involved
        if (forceCodeValues.includes('99')) {
          if (parsedValues.length > 1) {
            return 'NIBRS-2019.2-51';
          }
        }

        // NIBRS-2019.2-55: Weapon/Force Involved
        if (['09A', '09B', '09C'].includes(this.obj.offense.values.statuteDetails?.FBICode)) {
          if (forceCodeValues.includes('99')) {
            return 'NIBRS-2019.2-55';
          }
        }

        // Apply TEXAS validation when the agency is based on TEXAS
        if (ibrsVersion === 'TX') {
          // TEXAS-2023.1-01: Weapon/Force Involved
          if (
            ['100', '11A', '11B', '11C', '11D', '120', '13A', '13B', '520'].includes(
              this.obj.offense.values.statuteDetails?.FBICode
            )
          ) {
            if (forceCodeValues.includes('99')) {
              return 'TEXAS-2023.1-01';
            }
          }

          // TEXAS-2023.1-02: Weapon/Force Involved
          if (
            this.obj.offense.values.statuteDetails?.FBICode === '210' &&
            !forceCodeValues.includes('99')
          ) {
            return 'TEXAS-2023.1-02';
          }
        }

        // NIBRS-2019.2-53: Weapon/Force Involved
        if (
          ![
            '09A',
            '09B',
            '09C',
            '100',
            '11A',
            '11B',
            '11C',
            '11D',
            '120',
            '13A',
            '13B',
            '210',
            '520',
            '64A',
            '64B',
          ].includes(this.obj.offense.values.statuteDetails?.FBICode)
        ) {
          if (parsedValues.length) {
            return 'NIBRS-2019.2-53';
          }
        }

        // NIBRS-2019.2-56: Weapon/Force Involved
        if (this.obj.offense.values.statuteDetails?.FBICode === '13B') {
          if (
            forceCodeValues.includes('11') ||
            forceCodeValues.includes('12') ||
            forceCodeValues.includes('13') ||
            forceCodeValues.includes('14') ||
            forceCodeValues.includes('15') ||
            forceCodeValues.includes('11A') ||
            forceCodeValues.includes('12A') ||
            forceCodeValues.includes('13A') ||
            forceCodeValues.includes('14A') ||
            forceCodeValues.includes('15A')
          ) {
            return 'NIBRS-2019.2-56';
          }
        }

        // NIBRS-2019.2-54: Weapon/Force Involved
        // LIBRS-2019.2-07: Weapon/Force Involved
        if (this.obj.offense.values.statuteDetails?.FBICode === '13B') {
          let errorFlag = false;
          let commonAllowedForceCodeValues = ['40', '90', '95'];
          let forceCodeValuesToConsider =
            ibrsVersion !== 'LA'
              ? [...commonAllowedForceCodeValues, '99']
              : commonAllowedForceCodeValues;

          forceCodeValues.forEach((value) => {
            if (!forceCodeValuesToConsider.includes(value)) {
              errorFlag = true;
            }
          });
          if (errorFlag) {
            return ibrsVersion !== 'LA' ? 'NIBRS-2019.2-54' : 'LIBRS-2019.2-07';
          }
        }
      },
    },

    criminalActivity: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-49: Criminal Activity/Gang Information
        const ibrsVersion = store.store.getState().user?.ibrsVersion;
        const fbiCode = this.obj?.offense?.values?.statuteDetails?.FBICode;
        const crimeAgainst = this.obj?.offense?.values?.statuteDetails?.CrimeAgainst;
        const criminalActivityOffenses = new Set([
          '250',
          '280',
          '35A',
          '35B',
          '39C',
          '370',
          '520',
          '720',
          '09A',
          '09B',
          '100',
          '11A',
          '11B',
          '11C',
          '11D',
          '120',
          '13A',
          '13B',
          '13C',
        ]);

        /** Validate the criminal activity requirement based on IBRS version */
        const needsCriminalActivity = (ibrsVersion, fbiCode, crimeAgainst) => {
          const isLA = ibrsVersion === 'LA';
          const inGeneric = fbiCode && criminalActivityOffenses.has(fbiCode);
          const laExtra = fbiCode === '90A' || (fbiCode === '90Z' && crimeAgainst === 'PERSON');
          return isLA ? inGeneric || laExtra : inGeneric;
        };

        if (needsCriminalActivity(ibrsVersion, fbiCode, crimeAgainst)) {
          if (!this.value || !JSON.parse(this.value)?.length) {
            return 'NIBRS-2019.2-49';
          }
        }

        if (!this.value) return;
        const parsedValues = JSON.parse(this.value);
        if (!parsedValues) return;

        const codeCriminalActivity = store.store.getState().dictionary.codeCriminalActivities;
        const criminalActivityCodeValues =
          parsedValues?.map((value) => getCategoryOfCode(codeCriminalActivity, value)) || [];

        // NIBRS-2019.2-45: Criminal Activity/Gang Information
        if (criminalActivityCodeValues.includes('n')) {
          if (parsedValues.length > 1) {
            return 'NIBRS-2019.2-45';
          }
        }

        // NIBRS-2019.2-48: Criminal Activity/Gang Information
        if (
          criminalActivityCodeValues.includes('a') ||
          criminalActivityCodeValues.includes('f') ||
          criminalActivityCodeValues.includes('i') ||
          criminalActivityCodeValues.includes('s')
        ) {
          if (this.obj.offense.values.statuteDetails?.FBICode !== '720') {
            return 'NIBRS-2019.2-48';
          }
        }

        // NIBRS-2019.2-47: Criminal Activity/Gang Information
        if (
          criminalActivityCodeValues.includes('j') ||
          criminalActivityCodeValues.includes('g') ||
          criminalActivityCodeValues.includes('n')
        ) {
          if (
            !['09A', '09B', '100', '11A', '11B', '11C', '11D', '120', '13A', '13B', '13C'].includes(
              this.obj.offense.values.statuteDetails?.FBICode
            )
          ) {
            return 'NIBRS-2019.2-47';
          }
        }

        // NIBRS-2019.2-46: Criminal Activity/Gang Information
        if (
          criminalActivityCodeValues.includes('b') ||
          criminalActivityCodeValues.includes('c') ||
          criminalActivityCodeValues.includes('d') ||
          criminalActivityCodeValues.includes('e') ||
          criminalActivityCodeValues.includes('o') ||
          criminalActivityCodeValues.includes('p') ||
          criminalActivityCodeValues.includes('t') ||
          criminalActivityCodeValues.includes('u')
        ) {
          if (
            !['250', '280', '35A', '35B', '39C', '370', '520'].includes(
              this.obj.offense.values.statuteDetails?.FBICode
            )
          ) {
            return 'NIBRS-2019.2-46';
          }
        }
      },
    },

    methodOfEntry: {
      type: String,
      optional: true,
      custom() {
        // NIBRS-2019.2-42: Method of Entry
        if (this.obj.offense.values.statuteDetails?.FBICode === '220') {
          if (!this.value) {
            return 'NIBRS-2019.2-42';
          }
        }

        // NIBRS-2019.2-43: Method of Entry
        if (this.obj.offense.values.statuteDetails?.FBICode !== '220') {
          if (this.value) {
            return 'NIBRS-2019.2-43';
          }
        }
      },
    },

    offenseModifier: {
      type: String,
      optional: true,
      custom() {
        const ibrsVersion = store.store.getState().user?.ibrsVersion;

        // LIBRS-2019.2-06: Offense Modifiers for Group A Only
        if (ibrsVersion === 'LA') {
          const fbiCode = this.obj.offense.values.statuteDetails?.FBICode;

          if (!this.value || this.value.trim() === '') return;

          // If modifier is present and offense is NOT Group A, return error
          if (!groupAOffenseCodes.includes(fbiCode)) {
            return 'LIBRS-2019.2-06';
          }
        }
      },
    },

    statuteDetails: {
      type: String,
      optional: true,
      custom() {
        const codeLocationCategories = store.store.getState().dictionary.codeLocationCategories;
        const locationCategory = getCategoryOfCode(
          codeLocationCategories,
          this.obj.offense.values.locationCategory
        );

        //NIBRS-2019.2-39: Location
        if (locationCategory === '58') {
          if (
            ![
              '210',
              '250',
              '270',
              '280',
              '290',
              '370',
              '510',
              '26A',
              '26B',
              '26C',
              '26D',
              '26E',
              '26F',
              '26G',
              '39A',
              '39B',
              '39C',
              '13C',
              '35A',
              '35B',
              '520',
              '64A',
              '64B',
              '40A',
              '40B',
              '40C',
            ].includes(this.obj.offense.values.statuteDetails?.FBICode)
          ) {
            return 'NIBRS-2019.2-39';
          }
        }
      },
    },
  });
};
